import { useQueries, useQuery } from '@tanstack/react-query';

import * as FlashCalendarService from '~/services/FlashCalendarService';

import { BUNDLE_OCCUPANCIES } from '~/types/bundles/bundle';

export const flashAvailabilityQueryKeys = {
  all: ['flash-availability'] as const,
  stay: (stay: FlashCalendarService.FlashPackageStay | undefined, checkInDates: Array<string>) =>
    [...flashAvailabilityQueryKeys.all, stay, checkInDates] as const,
};

/** Rooms left and price per check-in date, for each occupancy the bundle checks (single and double). */
export type FlashAvailability = Record<number, Record<string, FlashCalendarService.FlashStayRate>>;

function flashAvailabilityQuery(stay: FlashCalendarService.FlashPackageStay | undefined, checkInDates: Array<string>) {
  return {
    queryKey: flashAvailabilityQueryKeys.stay(stay, checkInDates),
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<FlashAvailability> => {
      const entries = await Promise.all(
        BUNDLE_OCCUPANCIES.map(
          async (occupancy) =>
            [occupancy, await FlashCalendarService.getStayRates(stay, occupancy, checkInDates, { signal })] as const,
        ),
      );
      return Object.fromEntries(entries);
    },
    enabled: !!stay && checkInDates.length > 0,
    staleTime: 5 * 60 * 1000,
  };
}

export function useFlashAvailability(
  stay: FlashCalendarService.FlashPackageStay | undefined,
  checkInDates: Array<string>,
) {
  return useQuery(flashAvailabilityQuery(stay, checkInDates));
}

/** One availability check per hotel stay, e.g. every hotel in a bundle. */
export function useFlashAvailabilities(
  stays: Array<{ stay: FlashCalendarService.FlashPackageStay; checkInDates: Array<string> }>,
) {
  return useQueries({ queries: stays.map(({ stay, checkInDates }) => flashAvailabilityQuery(stay, checkInDates)) });
}
